import React, { useContext, useMemo, useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { DarkModeContext } from "../../context/DarkModeContext";
import { getToken } from "../../utils/auth";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCalendarDays, faChevronDown, faClock, faArrowLeft } from "@fortawesome/free-solid-svg-icons";
import { faWhatsapp } from "@fortawesome/free-brands-svg-icons";
import { ZMLayout, ZMCard, ZMTable, ZMBadge, ZMEmptyState, ZMLoadingState, ZMErrorState } from "./components/ZMUI";
import "./ZMCarga.css";

const ESTADOS_LEAD = [
    { value: "nuevo", label: "Nuevo" },
    { value: "contactado", label: "Contactado" },
    { value: "calificado", label: "Calificado" },
    { value: "demo", label: "Demo" },
    { value: "cerrado", label: "Cerrado / Ganado" },
    { value: "rechazado", label: "Rechazado" },
    { value: "sin_interes", label: "Sin interes" },
];

const TEMPERATURAS = ["Frio", "Tibio", "Caliente"];
const TIEMPOS_RESPUESTA = ["< 1 hora", "1-4 horas", "4-24 horas", "> 24 horas"];
const PROB_CIERRE = ["10%", "25%", "50%", "75%", "90%"];
const RESULTADOS = ["Ganado", "Perdido", "Pendiente"];
const OBJECIONES = ["Precio", "Tiempo", "Confianza", "Funcionalidad", "Integracion", "Otro"];
const MOTIVOS_PERDIDA = ["No responde", "Precio alto", "Sin presupuesto", "Se fue con competidor", "Sin urgencia"];
const TIPO_ORGANIZADOR = ["Productora", "Marca", "Espacio", "Agencia", "Freelance", "Otro"];
const TICKETERAS = ["Paseshow", "Passline", "Eventbrite", "Entradaplay", "TuEntrada", "Otra"];
const VENTA_ACTUAL = ["WhatsApp", "Transferencia", "Ambas", "No vende"];
const EVENTOS_MES = ["1-2", "3-5", "6-10", "+10"];
const TIPOS_EVENTO = ["Recital", "Festival", "Conferencia", "Fiesta", "Deportivo", "Teatro", "Otro"];
const FUENTES = ["Meta Lead Ads", "Landing", "Referido", "WhatsApp inbound", "Otro"];
const NECESIDADES = ["App", "QR", "Control de acceso", "Reportes"];

const EMPTY_FORM = {
    fechaHoraLead: "",
    nombreCompleto: "",
    telefonoWhatsapp: "",
    email: "",
    fuenteLead: "",
    campanaMeta: "",
    conjunto: "",
    anuncio: "",
    placement: "",
    paisProvincia: "",
    productoraMarcaEspacio: "",
    cargoContacto: "",
    ciudad: "",
    tipoOrganizador: "",
    ticketeraActual: "",
    vendeWhatsTransferencia: "",
    cantidadEventosMes: "",
    tipoEvento: "",
    nombreEvento: "",
    fechaEstimada: "",
    capacidadEstimada: "",
    ticketPromedio: "",
    facturacionEsperada: "",
    necesidades: [],
    estadoLead: "",
    temperatura: "",
    primerContacto: "",
    tiempoRespuesta: "",
    demoAgendada: "",
    probabilidadCierre: "",
    proximaAccion: "",
    proximoSeguimiento: "",
    resultadoFinal: "",
    motivoPerdida: "",
    competidor: "",
    objecionPrincipal: "",
    insightMarketing: "",
    comentariosCloser: "",
};

const normalizar = (v) =>
    String(v || "")
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "")
        .toLowerCase();

const formatDate = (v) => {
    if (!v) return "-";
    const d = new Date(v);
    if (Number.isNaN(d.getTime())) return "-";
    return d.toLocaleDateString("es-AR");
};

const toDateTimeInput = (v) => {
    if (!v) return "";
    const d = new Date(v);
    if (Number.isNaN(d.getTime())) return "";
    const tz = d.getTimezoneOffset() * 60000;
    return new Date(d.getTime() - tz).toISOString().slice(0, 16);
};

const toDateInput = (v) => {
    if (!v) return "";
    const d = new Date(v);
    if (Number.isNaN(d.getTime())) return "";
    const tz = d.getTimezoneOffset() * 60000;
    return new Date(d.getTime() - tz).toISOString().slice(0, 10);
};

const toNullableDate = (v) => (v ? new Date(v).toISOString() : null);

const toNullableInt = (v) => {
    const txt = String(v ?? "").trim();
    if (!txt) return null;
    const parsed = parseInt(txt.replace(/[^\d-]/g, ""), 10);
    return Number.isNaN(parsed) ? null : parsed;
};

const toNullableDecimal = (v) => {
    const txt = String(v ?? "").trim();
    if (!txt) return null;
    const raw = txt.replace(/[^\d.,-]/g, "");
    const normalized = raw.includes(",") && raw.includes(".")
        ? raw.replace(/\./g, "").replace(",", ".")
        : raw.replace(",", ".");
    const parsed = Number.parseFloat(normalized);
    return Number.isNaN(parsed) ? null : parsed;
};

const mapEstadoToSelect = (estado) => {
    const key = normalizar(estado);
    if (key === "contactado") return "contactado";
    if (key === "calificado" || key === "en seguimiento" || key === "en_seguimiento") return "calificado";
    if (key === "demo") return "demo";
    if (key === "cerrado" || key === "ganado") return "cerrado";
    if (key === "rechazado" || key === "perdido") return "rechazado";
    if (key === "sin interes" || key === "sin_interes") return "sin_interes";
    if (key === "nuevo") return "nuevo";
    return "";
};

const parseExtra = (lead) => {
    if (!lead?.datosExtra) return {};
    if (typeof lead.datosExtra === "object") return lead.datosExtra || {};
    try {
        return JSON.parse(lead.datosExtra);
    } catch {
        return {};
    }
};

const pickExtraValue = (lead, keys) => {
    const extra = parseExtra(lead);
    const campos = extra.campos && typeof extra.campos === "object" ? extra.campos : extra;
    const map = new Map(Object.entries(campos || {}).map(([k, v]) => [normalizar(k), v]));
    for (const key of keys) {
        const val = map.get(normalizar(key));
        if (val) return String(val);
    }
    return "";
};

function Section({ number, title, children }) {
    return (
        <section className="ztc-section">
            <div className="ztc-section-head">
                <span className="ztc-section-num">{number}</span>
                <h3>{title}</h3>
            </div>
            {children}
        </section>
    );
}

function SelectField({ label, name, value, onChange, options, placeholder, error }) {
    return (
        <div className="ztc-field">
            <label htmlFor={name}>{label}</label>
            <div className="ztc-select-wrap">
                <select
                    id={name}
                    name={name}
                    value={value}
                    onChange={onChange}
                    className={error ? "ztc-invalid" : ""}
                >
                    <option value="">{placeholder}</option>
                    {options.map((opt) => <option key={opt} value={opt}>{opt}</option>)}
                </select>
                <FontAwesomeIcon icon={faChevronDown} className="ztc-select-icon" />
            </div>
            {error && <small className="ztc-error">{error}</small>}
        </div>
    );
}

function DateField({ label, name, value, onChange, type = "date", error }) {
    return (
        <div className="ztc-field">
            <label htmlFor={name}>{label}</label>
            <div className="ztc-input-icon">
                <input
                    id={name}
                    name={name}
                    type={type}
                    value={value}
                    onChange={onChange}
                    className={error ? "ztc-invalid" : ""}
                />
                <FontAwesomeIcon icon={type === "datetime-local" ? faClock : faCalendarDays} />
            </div>
            {error && <small className="ztc-error">{error}</small>}
        </div>
    );
}

export default function ZMCarga() {
    const { darkMode } = useContext(DarkModeContext);
    const navegacion = useNavigate();
    const location = useLocation();

    const [autorizado, setAutorizado] = useState(false);
    const [cargando, setCargando] = useState(true);
    const [leads, setLeads] = useState([]);
    const [busqueda, setBusqueda] = useState("");
    const [estadoFiltro, setEstadoFiltro] = useState("todos");
    const [leadSeleccionado, setLeadSeleccionado] = useState(null);
    const [formData, setFormData] = useState(EMPTY_FORM);
    const [errores, setErrores] = useState({});
    const [guardando, setGuardando] = useState(false);
    const [cargandoFicha, setCargandoFicha] = useState(false);
    const [fichaExiste, setFichaExiste] = useState(false);
    const [mensaje, setMensaje] = useState(null);
    const [errorCarga, setErrorCarga] = useState("");
    const [leadIdQueryProcesado, setLeadIdQueryProcesado] = useState(null);

    const apiLeads = process.env.REACT_APP_API_ZT_LEADS;
    const apiFichaBase = process.env.REACT_APP_API_ZT_LEADS_ESTADO || process.env.REACT_APP_API_ZT_LEADS;

    const cargarLeads = async (token) => {
        const r = await fetch(apiLeads, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ Token: token }),
        });
        if (!r.ok) throw new Error("No se pudo cargar leads");
        const data = await r.json();
        return Array.isArray(data) ? data : [];
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => {
        const init = async () => {
            const token = getToken();
            if (!token) {
                navegacion("/");
                return;
            }
            try {
                setErrorCarga("");
                const r = await fetch(apiLeads, {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify({ Token: token }),
                });
                if (r.status === 401 || r.status === 403) {
                    navegacion("/");
                    return;
                }
                if (r.ok) {
                    const data = await r.json();
                    setLeads(Array.isArray(data) ? data : []);
                    setAutorizado(true);
                } else {
                    setErrorCarga("No se pudo cargar el listado de leads.");
                }
            } catch {
                setErrorCarga("No se pudo cargar el listado de leads.");
            } finally {
                setCargando(false);
            }
        };
        init();
    }, [navegacion, apiLeads]);

    const listaCampanas = useMemo(() => [...new Set(leads.map((l) => l.campaignName || l.campaignId).filter(Boolean))], [leads]);
    const listaConjuntos = useMemo(() => [...new Set(leads.map((l) => l.adsetName || l.adsetId).filter(Boolean))], [leads]);
    const listaAnuncios = useMemo(() => [...new Set(leads.map((l) => l.adName || l.adId).filter(Boolean))], [leads]);

    const leadsFiltrados = useMemo(() => {
        const txt = normalizar(busqueda.trim());
        return leads.filter((l) => {
            const coincideEstado = estadoFiltro === "todos" || l.estado === estadoFiltro;
            if (!coincideEstado) return false;
            if (!txt) return true;
            return [l.nombre, l.empresa, l.telefono, l.email, l.campaignId, l.campaignName, l.formId, l.formName, l.adId, l.adName, l.adsetId, l.adsetName, l.pageId]
                .some((v) => normalizar(v).includes(txt));
        });
    }, [leads, busqueda, estadoFiltro]);

    const cargarBaseLead = (lead) => ({
            ...EMPTY_FORM,
            fechaHoraLead: toDateTimeInput(lead.fechaRecibido),
            nombreCompleto: lead.nombre || "",
            telefonoWhatsapp: lead.telefono || "",
            email: lead.email || "",
            fuenteLead: "Meta Lead Ads",
            campanaMeta: lead.campaignName || lead.campaignId || "",
            conjunto: lead.adsetName || lead.adsetId || pickExtraValue(lead, ["adset_name", "adset", "conjunto"]),
            anuncio: lead.adName || lead.adId || pickExtraValue(lead, ["ad_name", "anuncio"]),
            placement: pickExtraValue(lead, ["placement", "platform_position"]),
            paisProvincia: pickExtraValue(lead, ["province", "provincia", "state", "pais"]),
            productoraMarcaEspacio: lead.empresa || pickExtraValue(lead, ["company_name", "empresa"]),
            cargoContacto: pickExtraValue(lead, ["job_title", "cargo"]),
            ciudad: pickExtraValue(lead, ["city", "ciudad"]),
            estadoLead: lead.estado || "nuevo",
            comentariosCloser: lead.observaciones || "",
        });

    const mapFichaToForm = (ficha) => {
        const safe = ficha || {};
        const necesidades = [];
        if (safe.necesitaApp) necesidades.push("App");
        if (safe.necesitaQR) necesidades.push("QR");
        if (safe.necesitaControlAcceso) necesidades.push("Control de acceso");
        if (safe.necesitaReportes) necesidades.push("Reportes");

        return {
            productoraMarcaEspacio: safe.productora || "",
            cargoContacto: safe.cargoContacto || "",
            ciudad: safe.ciudad || "",
            tipoOrganizador: safe.tipoOrganizador || "",
            ticketeraActual: safe.ticketeraActual || "",
            vendeWhatsTransferencia: safe.vendePor || "",
            cantidadEventosMes: safe.eventosPorMes || "",
            tipoEvento: safe.tipoEvento || "",
            nombreEvento: safe.nombreEvento || "",
            fechaEstimada: toDateInput(safe.fechaEstimada),
            capacidadEstimada: safe.capacidadEstimada ?? "",
            ticketPromedio: safe.ticketPromedio ?? "",
            facturacionEsperada: safe.facturacionEsperada ?? "",
            necesidades,
            estadoLead: mapEstadoToSelect(safe.estado),
            temperatura: safe.temperatura || "",
            primerContacto: toDateInput(safe.primerContacto),
            tiempoRespuesta: safe.tiempoRespuesta || "",
            demoAgendada: toDateInput(safe.demoAgendada),
            probabilidadCierre: safe.probabilidadCierre || "",
            proximaAccion: safe.proximaAccion || "",
            proximoSeguimiento: toDateInput(safe.proximoSeguimiento),
            resultadoFinal: safe.resultadoFinal || "",
            motivoPerdida: safe.motivoPerdida || "",
            competidor: safe.competidor || "",
            objecionPrincipal: safe.objecionPrincipal || "",
            insightMarketing: safe.insightMarketing || "",
            comentariosCloser: safe.comentariosCloser || "",
        };
    };

    const fetchFichaLead = async (leadId, token) => {
        const r = await fetch(`${apiFichaBase}/${leadId}/ficha?token=${encodeURIComponent(token)}`);
        if (r.status === 401 || r.status === 403) {
            navegacion("/");
            return { existe: false, ficha: null };
        }
        if (!r.ok) throw new Error("No se pudo cargar ficha");
        const payload = await r.json();
        return {
            existe: !!payload?.existe,
            ficha: payload?.ficha || null,
        };
    };

    const abrirFicha = async (lead) => {
        const token = getToken();
        if (!token) {
            navegacion("/");
            return;
        }
        setLeadSeleccionado(lead);
        setCargandoFicha(true);
        const base = cargarBaseLead(lead);
        setFormData(base);
        setErrores({});
        setMensaje(null);
        try {
            const { existe, ficha } = await fetchFichaLead(lead.id, token);
            setFichaExiste(existe);
            if (ficha) {
                const mapped = mapFichaToForm(ficha);
                setFormData((prev) => ({
                    ...prev,
                    ...mapped,
                    estadoLead: mapped.estadoLead || prev.estadoLead,
                }));
            }
            window.scrollTo({ top: 0, behavior: "smooth" });
        } catch {
            setFichaExiste(false);
            setMensaje({ tipo: "warning", texto: "No se pudo cargar la ficha guardada. PodÃ©s continuar editando." });
        } finally {
            setCargandoFicha(false);
        }
    };

    const volverALaTabla = () => {
        setLeadSeleccionado(null);
        setFichaExiste(false);
        setErrores({});
        setMensaje(null);
        setLeadIdQueryProcesado(null);
        if (location.search) navegacion("/zocometa/carga", { replace: true });
    };

    /* eslint-disable react-hooks/exhaustive-deps */
    useEffect(() => {
        if (cargando || !autorizado || !leads.length || leadSeleccionado) return;
        const leadIdQuery = new URLSearchParams(location.search).get("leadId");
        if (!leadIdQuery || leadIdQueryProcesado === leadIdQuery) return;

        const targetId = Number.parseInt(leadIdQuery, 10);
        setLeadIdQueryProcesado(leadIdQuery);
        if (Number.isNaN(targetId)) return;

        const lead = leads.find((x) => Number(x.id) === targetId);
        if (lead) abrirFicha(lead);
    }, [cargando, autorizado, leads, leadSeleccionado, location.search, leadIdQueryProcesado]);
    /* eslint-enable react-hooks/exhaustive-deps */

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({ ...prev, [name]: value }));
        setErrores((prev) => ({ ...prev, [name]: "" }));
    };

    const onTextareaChange = (e) => {
        handleChange(e);
        e.target.style.height = "auto";
        e.target.style.height = `${e.target.scrollHeight}px`;
    };

    const toggleNecesidad = (item) => {
        setFormData((prev) => {
            const set = new Set(prev.necesidades);
            if (set.has(item)) set.delete(item);
            else set.add(item);
            return { ...prev, necesidades: [...set] };
        });
    };

    const validarSubmit = () => {
        const next = {};
        if (!formData.fechaHoraLead) next.fechaHoraLead = "Requerido";
        if (!formData.nombreCompleto.trim()) next.nombreCompleto = "Requerido";
        if (!formData.fuenteLead) next.fuenteLead = "Requerido";
        if (!formData.campanaMeta) next.campanaMeta = "Requerido";
        if (!formData.ticketeraActual) next.ticketeraActual = "Requerido";
        if (!formData.estadoLead) next.estadoLead = "Requerido";
        if (!formData.telefonoWhatsapp.trim() && !formData.email.trim()) {
            next.telefonoWhatsapp = "Completa telefono o email";
            next.email = "Completa telefono o email";
        }
        if (formData.resultadoFinal === "Perdido" && !formData.motivoPerdida) next.motivoPerdida = "Obligatorio si se pierde";
        setErrores(next);
        return Object.keys(next).length === 0;
    };

    const construirPayloadFicha = (token, esBorrador) => ({
        Token: token,
        Productora: formData.productoraMarcaEspacio || null,
        CargoContacto: formData.cargoContacto || null,
        Ciudad: formData.ciudad || null,
        TipoOrganizador: formData.tipoOrganizador || null,
        TicketeraActual: formData.ticketeraActual || null,
        VendePor: formData.vendeWhatsTransferencia || null,
        EventosPorMes: formData.cantidadEventosMes || null,
        TipoEvento: formData.tipoEvento || null,
        NombreEvento: formData.nombreEvento || null,
        FechaEstimada: toNullableDate(formData.fechaEstimada),
        CapacidadEstimada: toNullableInt(formData.capacidadEstimada),
        TicketPromedio: toNullableDecimal(formData.ticketPromedio),
        FacturacionEsperada: toNullableDecimal(formData.facturacionEsperada),
        NecesitaApp: formData.necesidades.includes("App"),
        NecesitaQR: formData.necesidades.includes("QR"),
        NecesitaControlAcceso: formData.necesidades.includes("Control de acceso"),
        NecesitaReportes: formData.necesidades.includes("Reportes"),
        Temperatura: formData.temperatura || null,
        PrimerContacto: toNullableDate(formData.primerContacto),
        TiempoRespuesta: formData.tiempoRespuesta || null,
        DemoAgendada: toNullableDate(formData.demoAgendada),
        ProbabilidadCierre: formData.probabilidadCierre || null,
        ProximaAccion: formData.proximaAccion || null,
        ProximoSeguimiento: toNullableDate(formData.proximoSeguimiento),
        ResultadoFinal: formData.resultadoFinal || null,
        MotivoPerdida: formData.motivoPerdida || null,
        Competidor: formData.competidor || null,
        ObjecionPrincipal: formData.objecionPrincipal || null,
        InsightMarketing: formData.insightMarketing || null,
        ComentariosCloser: formData.comentariosCloser || null,
        Estado: formData.estadoLead || null,
        EsBorrador: esBorrador,
    });

    const guardarFichaApi = async ({ esBorrador }) => {
        if (!leadSeleccionado) return false;
        const token = getToken();
        if (!token) return false;

        setGuardando(true);
        setMensaje(null);

        try {
            const payload = construirPayloadFicha(token, esBorrador);
            const method = fichaExiste ? "PUT" : "POST";
            const r = await fetch(`${apiFichaBase}/${leadSeleccionado.id}/ficha`, {
                method,
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(payload),
            });
            if (!r.ok) throw new Error("No se pudo guardar ficha");

            const data = await r.json();
            const estadoActualizado = data?.estadoLead || formData.estadoLead;
            setFichaExiste(true);

            const leadsActualizados = await cargarLeads(token);
            setLeads(leadsActualizados);
            const leadNuevo = leadsActualizados.find((x) => x.id === leadSeleccionado.id)
                || { ...leadSeleccionado, estado: estadoActualizado, observaciones: formData.comentariosCloser || leadSeleccionado.observaciones };
            setLeadSeleccionado(leadNuevo);
            setFormData((prev) => ({ ...prev, estadoLead: estadoActualizado }));
            return true;
        } catch {
            return false;
        } finally {
            setGuardando(false);
        }
    };

    const guardarBorrador = async () => {
        if (!leadSeleccionado) return;
        const ok = await guardarFichaApi({ esBorrador: true });
        setMensaje(ok ? { tipo: "success", texto: "Borrador guardado." } : { tipo: "danger", texto: "No se pudo guardar el borrador." });
    };

    const guardarYEnviar = async () => {
        if (!validarSubmit()) {
            setMensaje({ tipo: "danger", texto: "Revisa los campos obligatorios." });
            return;
        }
        const ok = await guardarFichaApi({ esBorrador: false });
        if (ok) {
            setMensaje({ tipo: "success", texto: "Ficha guardada y enviada a analisis." });
        } else {
            setMensaje({ tipo: "danger", texto: "No se pudo enviar a analisis." });
        }
    };

    const checklist = [
        { label: "Fuente", ok: !!formData.fuenteLead },
        { label: "Campana", ok: !!formData.campanaMeta },
        { label: "Conjunto", ok: !!formData.conjunto },
        { label: "Anuncio", ok: !!formData.anuncio },
        { label: "Fecha lead", ok: !!formData.fechaHoraLead },
        { label: "Primer contacto", ok: !!formData.primerContacto },
        { label: "Estado", ok: !!formData.estadoLead },
        { label: "Ticketera actual", ok: !!formData.ticketeraActual },
        { label: "Tipo evento", ok: !!formData.tipoEvento },
        { label: "Fecha evento", ok: !!formData.fechaEstimada },
        { label: "Presupuesto estimado", ok: !!formData.facturacionEsperada },
        { label: "Motivo de perdida", ok: formData.resultadoFinal !== "Perdido" || !!formData.motivoPerdida },
    ];
    const completitud = Math.round((checklist.filter((c) => c.ok).length / checklist.length) * 100);
    const faltantes = checklist.filter((item) => !item.ok);
    const leadScore = Math.min(100, Math.max(8, Math.round(
        completitud * 0.55
        + (formData.probabilidadCierre ? parseInt(formData.probabilidadCierre, 10) * 0.35 : 0)
        + (formData.demoAgendada ? 10 : 0)
        + (formData.ticketeraActual ? 6 : 0)
    )));
    const fitComercial = leadScore >= 75 ? "Alto" : leadScore >= 50 ? "Medio" : "Bajo";
    const prioridadComercial = formData.estadoLead === "nuevo" && !formData.primerContacto
        ? "Alta"
        : formData.probabilidadCierre === "75%" || formData.probabilidadCierre === "90%"
            ? "Alta"
            : formData.demoAgendada || formData.estadoLead === "calificado"
                ? "Media"
                : "Baja";
    const resultadoTimeline = formData.resultadoFinal
        ? formData.resultadoFinal
        : formData.estadoLead === "cerrado"
            ? "Ganado"
            : formData.estadoLead === "rechazado" || formData.estadoLead === "sin_interes"
                ? "Perdido"
                : "Pendiente";

    if (cargando) {
        return <ZMLoadingState label="Cargando carga del closer..." fullScreen />;
    }
    if (errorCarga) return <ZMErrorState message={errorCarga} onRetry={() => window.location.reload()} />;
    if (!autorizado) return null;

    if (!leadSeleccionado) {
        return (
            <ZMLayout
                title="Carga del closer"
                subtitle="Selecciona un lead para abrir su ficha completa y gestionar la oportunidad."
                right={`${leadsFiltrados.length} de ${leads.length} leads`}
                className={darkMode ? "container-dark" : ""}
            >
                <ZMCard>
                    <div className="row g-2 mb-3">
                        <div className="col-12 col-md-8">
                            <input
                                className="form-control form-control-sm"
                                placeholder="Buscar por nombre, email, telefono, empresa o campana..."
                                value={busqueda}
                                onChange={(e) => setBusqueda(e.target.value)}
                            />
                        </div>
                        <div className="col-12 col-md-4">
                            <select className="form-select form-select-sm zm-carga-top-select" value={estadoFiltro} onChange={(e) => setEstadoFiltro(e.target.value)}>
                                <option value="todos">Todos los estados</option>
                                {ESTADOS_LEAD.map((e) => <option key={e.value} value={e.value}>{e.label}</option>)}
                            </select>
                        </div>
                    </div>

                    {leadsFiltrados.length === 0 ? (
                        <ZMEmptyState title="Sin resultados" hint="Ajusta la busqueda o el estado para encontrar leads." />
                    ) : (
                        <ZMTable
                            columns={[
                                { key: "fechaRecibido", header: "Fecha", render: (l) => formatDate(l.fechaRecibido) },
                                { key: "nombre", header: "Nombre", render: (l) => <strong>{l.nombre || "(sin nombre)"}</strong> },
                                { key: "telefono", header: "Telefono / WhatsApp", render: (l) => l.telefono || "-" },
                                { key: "email", header: "Email", render: (l) => l.email || "-" },
                                { key: "empresa", header: "Empresa", render: (l) => l.empresa || "-" },
                                { key: "campaignName", header: "Campana", render: (l) => l.campaignName || l.campaignId || "-" },
                                {
                                    key: "estado",
                                    header: "Estado",
                                    render: (l) => <ZMBadge status={l.estado} label={ESTADOS_LEAD.find((e) => e.value === l.estado)?.label || l.estado || "-"} />,
                                },
                                {
                                    key: "accion",
                                    header: "Accion",
                                    render: (l) => (
                                        <button type="button" className="btn btn-sm btn-outline-secondary" onClick={() => abrirFicha(l)}>
                                            Gestionar
                                        </button>
                                    ),
                                },
                            ]}
                            rows={leadsFiltrados}
                        />
                    )}
                </ZMCard>
            </ZMLayout>
        );
    }

    return (
        <div className={darkMode ? "container-dark p-3" : "p-3"}>
            <div className="ztc-dashboard-page">
                <div className="ztc-page-head">
                    <div>
                        <h2>Carga del closer</h2>
                        <p>Completa esta ficha para analizar calidad del lead, performance de campana y motivos de cierre.</p>
                    </div>
                    <button type="button" className="btn btn-light border btn-sm" onClick={volverALaTabla}>
                        <FontAwesomeIcon icon={faArrowLeft} className="me-2" />
                        Volver a leads
                    </button>
                </div>

                <div className="ztc-dashboard-layout">
                    <main className="ztc-main-column">
                        {cargandoFicha && <div className="alert alert-info py-2">Cargando ficha guardada...</div>}

                        <Section number={1} title="Datos del lead">
                            <div className="ztc-grid cols-4">
                                <DateField label="Fecha y hora del lead" name="fechaHoraLead" type="datetime-local" value={formData.fechaHoraLead} onChange={handleChange} error={errores.fechaHoraLead} />
                                <div className="ztc-field">
                                    <label htmlFor="nombreCompleto">Nombre completo</label>
                                    <input id="nombreCompleto" name="nombreCompleto" value={formData.nombreCompleto} onChange={handleChange} placeholder="Nombre y apellido" className={errores.nombreCompleto ? "ztc-invalid" : ""} />
                                    {errores.nombreCompleto && <small className="ztc-error">{errores.nombreCompleto}</small>}
                                </div>
                                <div className="ztc-field">
                                    <label htmlFor="telefonoWhatsapp">Telefono / WhatsApp</label>
                                    <div className="ztc-input-icon">
                                        <input id="telefonoWhatsapp" name="telefonoWhatsapp" value={formData.telefonoWhatsapp} onChange={handleChange} placeholder="+54 9 11 2345 6789" className={errores.telefonoWhatsapp ? "ztc-invalid" : ""} />
                                        <FontAwesomeIcon icon={faWhatsapp} />
                                    </div>
                                    {errores.telefonoWhatsapp && <small className="ztc-error">{errores.telefonoWhatsapp}</small>}
                                </div>
                                <div className="ztc-field">
                                    <label htmlFor="email">Email</label>
                                    <input id="email" name="email" type="email" value={formData.email} onChange={handleChange} placeholder="email@ejemplo.com" className={errores.email ? "ztc-invalid" : ""} />
                                    {errores.email && <small className="ztc-error">{errores.email}</small>}
                                </div>
                            </div>
                            <div className="ztc-grid cols-6">
                                <SelectField label="Fuente del lead" name="fuenteLead" value={formData.fuenteLead} onChange={handleChange} options={FUENTES} placeholder="Seleccionar fuente" error={errores.fuenteLead} />
                                <SelectField label="Campana Meta" name="campanaMeta" value={formData.campanaMeta} onChange={handleChange} options={listaCampanas.length ? listaCampanas : ["Sin campanas disponibles"]} placeholder="Seleccionar campana" error={errores.campanaMeta} />
                                <SelectField label="Conjunto" name="conjunto" value={formData.conjunto} onChange={handleChange} options={listaConjuntos.length ? listaConjuntos : ["Sin conjuntos disponibles"]} placeholder="Seleccionar conjunto" />
                                <SelectField label="Anuncio" name="anuncio" value={formData.anuncio} onChange={handleChange} options={listaAnuncios.length ? listaAnuncios : ["Sin anuncios disponibles"]} placeholder="Seleccionar anuncio" />
                                <SelectField label="Placement" name="placement" value={formData.placement} onChange={handleChange} options={["Feed", "Stories", "Reels", "Messenger", "Audience Network"]} placeholder="Seleccionar placement" />
                                <SelectField label="Pais / Provincia" name="paisProvincia" value={formData.paisProvincia} onChange={handleChange} options={["Argentina", "Buenos Aires", "Cordoba", "Santa Fe", "Mendoza", "Otra"]} placeholder="Seleccionar pais" />
                            </div>
                        </Section>

                        <Section number={2} title="Organizador / negocio">
                            <div className="ztc-grid cols-4">
                                <div className="ztc-field"><label htmlFor="productoraMarcaEspacio">Productora / marca / espacio</label><input id="productoraMarcaEspacio" name="productoraMarcaEspacio" value={formData.productoraMarcaEspacio} onChange={handleChange} placeholder="Nombre de la productora" /></div>
                                <div className="ztc-field"><label htmlFor="cargoContacto">Cargo del contacto</label><input id="cargoContacto" name="cargoContacto" value={formData.cargoContacto} onChange={handleChange} placeholder="Ej: Director, Founder, Manager" /></div>
                                <div className="ztc-field"><label htmlFor="ciudad">Ciudad</label><input id="ciudad" name="ciudad" value={formData.ciudad} onChange={handleChange} placeholder="Ej: Buenos Aires" /></div>
                                <SelectField label="Tipo de organizador" name="tipoOrganizador" value={formData.tipoOrganizador} onChange={handleChange} options={TIPO_ORGANIZADOR} placeholder="Seleccionar tipo" />
                            </div>
                            <div className="ztc-grid cols-3">
                                <SelectField label="Ticketera actual" name="ticketeraActual" value={formData.ticketeraActual} onChange={handleChange} options={TICKETERAS} placeholder="Seleccionar ticketera" error={errores.ticketeraActual} />
                                <SelectField label="Hoy vende por WhatsApp o transferencia?" name="vendeWhatsTransferencia" value={formData.vendeWhatsTransferencia} onChange={handleChange} options={VENTA_ACTUAL} placeholder="Seleccionar opcion" />
                                <SelectField label="Cantidad de eventos por mes" name="cantidadEventosMes" value={formData.cantidadEventosMes} onChange={handleChange} options={EVENTOS_MES} placeholder="Seleccionar cantidad" />
                            </div>
                        </Section>

                        <Section number={3} title="Datos del evento / oportunidad">
                            <div className="ztc-grid cols-4">
                                <SelectField label="Tipo de evento" name="tipoEvento" value={formData.tipoEvento} onChange={handleChange} options={TIPOS_EVENTO} placeholder="Seleccionar tipo" />
                                <div className="ztc-field"><label htmlFor="nombreEvento">Nombre del evento</label><input id="nombreEvento" name="nombreEvento" value={formData.nombreEvento} onChange={handleChange} placeholder="Nombre del evento" /></div>
                                <DateField label="Fecha estimada" name="fechaEstimada" value={formData.fechaEstimada} onChange={handleChange} />
                                <div className="ztc-field"><label htmlFor="capacidadEstimada">Capacidad estimada</label><input id="capacidadEstimada" name="capacidadEstimada" value={formData.capacidadEstimada} onChange={handleChange} placeholder="Ej. 1000" /></div>
                            </div>
                            <div className="ztc-grid cols-2">
                                <div className="ztc-field"><label htmlFor="ticketPromedio">Ticket promedio</label><input id="ticketPromedio" name="ticketPromedio" value={formData.ticketPromedio} onChange={handleChange} placeholder="$ 0.00" /></div>
                                <div className="ztc-field"><label htmlFor="facturacionEsperada">Facturacion esperada</label><input id="facturacionEsperada" name="facturacionEsperada" value={formData.facturacionEsperada} onChange={handleChange} placeholder="$ 0.00" /></div>
                            </div>
                            <div className="ztc-field">
                                <label>Necesita app / QR / control de acceso / reportes</label>
                                <div className="ztc-chip-group">
                                    {NECESIDADES.map((item) => (
                                        <button type="button" key={item} className={`ztc-chip ${formData.necesidades.includes(item) ? "active" : ""}`} onClick={() => toggleNecesidad(item)}>
                                            {item}
                                        </button>
                                    ))}
                                </div>
                            </div>
                        </Section>

                        <Section number={4} title="Gestion comercial">
                            <div className="ztc-grid cols-4">
                                <div className="ztc-field">
                                    <label htmlFor="estadoLead">Estado del lead</label>
                                    <div className="ztc-select-wrap">
                                        <select id="estadoLead" name="estadoLead" value={formData.estadoLead} onChange={handleChange} className={errores.estadoLead ? "ztc-invalid" : ""}>
                                            <option value="">Seleccionar estado</option>
                                            {ESTADOS_LEAD.map((estado) => <option key={estado.value} value={estado.value}>{estado.label}</option>)}
                                        </select>
                                        <FontAwesomeIcon icon={faChevronDown} className="ztc-select-icon" />
                                    </div>
                                    {errores.estadoLead && <small className="ztc-error">{errores.estadoLead}</small>}
                                </div>
                                <SelectField label="Temperatura" name="temperatura" value={formData.temperatura} onChange={handleChange} options={TEMPERATURAS} placeholder="Seleccionar temperatura" />
                                <DateField label="Primer contacto" name="primerContacto" value={formData.primerContacto} onChange={handleChange} />
                                <SelectField label="Tiempo de respuesta" name="tiempoRespuesta" value={formData.tiempoRespuesta} onChange={handleChange} options={TIEMPOS_RESPUESTA} placeholder="Seleccionar tiempo" />
                            </div>
                            <div className="ztc-grid cols-4">
                                <DateField label="Demo agendada" name="demoAgendada" value={formData.demoAgendada} onChange={handleChange} />
                                <SelectField label="Probabilidad de cierre" name="probabilidadCierre" value={formData.probabilidadCierre} onChange={handleChange} options={PROB_CIERRE} placeholder="Seleccionar %" />
                                <div className="ztc-field"><label htmlFor="proximaAccion">Proxima accion</label><input id="proximaAccion" name="proximaAccion" value={formData.proximaAccion} onChange={handleChange} placeholder="Que haremos?" /></div>
                                <DateField label="Proximo seguimiento" name="proximoSeguimiento" value={formData.proximoSeguimiento} onChange={handleChange} />
                            </div>
                        </Section>

                        <Section number={5} title="Resultado y aprendizaje">
                            <div className="ztc-grid cols-4">
                                <SelectField label="Resultado final" name="resultadoFinal" value={formData.resultadoFinal} onChange={handleChange} options={RESULTADOS} placeholder="Seleccionar resultado" />
                                <SelectField label="Motivo de perdida" name="motivoPerdida" value={formData.motivoPerdida} onChange={handleChange} options={MOTIVOS_PERDIDA} placeholder="Seleccionar motivo" error={errores.motivoPerdida} />
                                <div className="ztc-field"><label htmlFor="competidor">Competidor</label><input id="competidor" name="competidor" value={formData.competidor} onChange={handleChange} placeholder="Nombre del competidor" /></div>
                                <SelectField label="Objecion principal" name="objecionPrincipal" value={formData.objecionPrincipal} onChange={handleChange} options={OBJECIONES} placeholder="Seleccionar objecion" />
                            </div>
                            <div className="ztc-grid cols-2">
                                <div className="ztc-field"><label htmlFor="insightMarketing">Insight para marketing</label><textarea id="insightMarketing" name="insightMarketing" value={formData.insightMarketing} onChange={onTextareaChange} placeholder="Que podemos comunicar o mejorar?" rows={2} /></div>
                                <div className="ztc-field"><label htmlFor="comentariosCloser">Comentarios del closer</label><textarea id="comentariosCloser" name="comentariosCloser" value={formData.comentariosCloser} onChange={onTextareaChange} placeholder="Comentarios adicionales sobre el lead y el proceso" rows={2} /></div>
                            </div>
                        </Section>

                        {mensaje && <div className={`alert alert-${mensaje.tipo} py-2 mb-0`}>{mensaje.texto}</div>}
                    </main>

                    <aside className="ztc-side-column">
                        <div className="ztc-summary ztc-sticky-panel">
                            <h4>Resumen para analisis</h4>
                            <div className="ztc-summary-block">
                                <span>Completitud</span>
                                <strong>{completitud}%</strong>
                                <div className="ztc-progress"><div style={{ width: `${completitud}%` }} /></div>
                            </div>
                            <div className="ztc-summary-block">
                                <div className="ztc-metric-inline">
                                    <div>
                                        <span>Lead score</span>
                                        <strong className="ztc-mini-score">{leadScore}</strong>
                                    </div>
                                    <div>
                                        <span>Fit comercial</span>
                                        <b>{fitComercial}</b>
                                    </div>
                                    <div>
                                        <span>Prioridad</span>
                                        <b>{prioridadComercial}</b>
                                    </div>
                                </div>
                            </div>
                            <div className="ztc-summary-block">
                                <h5>Campos obligatorios</h5>
                                {checklist.map((item) => (
                                    <div key={item.label} className="ztc-check-item">
                                        <span className={item.ok ? "ok" : ""}>{item.ok ? "OK" : "O"}</span>
                                        <p>{item.label}</p>
                                    </div>
                                ))}
                                {faltantes.length > 0 && (
                                    <div className="ztc-missing-list">
                                        <small>Faltan: {faltantes.map((item) => item.label).join(", ")}</small>
                                    </div>
                                )}
                            </div>
                            <div className="ztc-summary-block">
                                <h5>Linea de tiempo</h5>
                                <div className="ztc-time-item"><strong>Lead recibido</strong><span>{formatDate(leadSeleccionado.fechaRecibido)}</span></div>
                                <div className="ztc-time-item"><strong>Primer contacto</strong><span>{formData.primerContacto ? formatDate(formData.primerContacto) : "Pendiente"}</span></div>
                                <div className="ztc-time-item"><strong>Demo</strong><span>{formData.demoAgendada ? formatDate(formData.demoAgendada) : "Pendiente"}</span></div>
                                <div className="ztc-time-item"><strong>Seguimiento</strong><span>{formData.proximoSeguimiento ? formatDate(formData.proximoSeguimiento) : "Pendiente"}</span></div>
                                <div className="ztc-time-item"><strong>Cierre</strong><span>{resultadoTimeline}</span></div>
                            </div>
                            <div className="ztc-summary-block">
                                <h5>Contexto del lead</h5>
                                <div className="ztc-time-item"><strong>Campana</strong><span>{leadSeleccionado.campaignName || leadSeleccionado.campaignId || "Sin campana"}</span></div>
                                <div className="ztc-time-item"><strong>Anuncio</strong><span>{leadSeleccionado.adName || leadSeleccionado.adId || "Sin anuncio"}</span></div>
                                <div className="ztc-time-item"><strong>Empresa</strong><span>{leadSeleccionado.empresa || "Sin empresa"}</span></div>
                            </div>
                        </div>

                        <div className="ztc-summary ztc-actions-panel">
                            <button type="button" className="btn btn-outline-secondary w-100 mb-2" onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}>
                                Ver ficha del lead
                            </button>
                            <button type="button" className="btn btn-light border w-100 mb-2" onClick={guardarBorrador} disabled={guardando || cargandoFicha}>
                                Guardar borrador
                            </button>
                            <button type="button" className="btn ztc-cta-btn w-100" onClick={guardarYEnviar} disabled={guardando || cargandoFicha}>
                                {guardando ? "Guardando..." : "Guardar y enviar a analisis"}
                            </button>
                        </div>
                    </aside>
                </div>
            </div>
        </div>
    );
}

